/**
 * Single source of truth for routes + entity → URL mapping, so cross-entity
 * "related" links never hard-code a path and stay consistent with the router.
 */

export type EntityKey =
  | "user"
  | "wallet"
  | "card"
  | "payment"
  | "cardRefund"
  | "sessionKey"
  | "ownerOperation"
  | "settlementObligation"
  | "settlement"
  | "reserveTopUpLeg"
  | "smartAccountOperation"
  | "hold"
  | "deposit"
  | "withdrawal"
  | "virtualAccount"
  | "paymentAccount"
  | "forwarderSweep"
  | "offrampBurn"
  | "depositAddress"
  | "stablecoinReceiveAddress"
  | "crossChainDeposit"
  | "crossChainSend"
  | "walletMigration"
  | "ownerKeyBackup"
  | "kycVerification"
  | "ledgerEntry"
  | "walletTransaction"
  | "cardNotification"
  | "earnPosition"
  | "earnOperation"
  | "vaultApproval"
  | "vault";

/** List + detail base segments per entity. */
const ENTITY_ROUTES: Record<EntityKey, { list: string; hasDetail: boolean; label: string }> = {
  user: { list: "/users", hasDetail: true, label: "User" },
  wallet: { list: "/wallets", hasDetail: true, label: "Wallet" },
  card: { list: "/cards", hasDetail: true, label: "Card" },
  payment: { list: "/payments", hasDetail: true, label: "Payment" },
  cardRefund: { list: "/card-refunds", hasDetail: true, label: "Card refund" },
  sessionKey: { list: "/session-keys", hasDetail: true, label: "Session Key" },
  ownerOperation: { list: "/owner-ops", hasDetail: true, label: "Owner Op" },
  settlementObligation: { list: "/settlements", hasDetail: true, label: "Settlement Obligation" },
  settlement: { list: "/settlement-executions", hasDetail: true, label: "Settlement Execution" },
  reserveTopUpLeg: { list: "/reserve-top-up-legs", hasDetail: true, label: "Reserve Top-Up Leg" },
  smartAccountOperation: { list: "/smart-account-ops", hasDetail: true, label: "Smart-Account Op" },
  hold: { list: "/holds", hasDetail: true, label: "Hold" },
  deposit: { list: "/deposits", hasDetail: true, label: "Deposit" },
  withdrawal: { list: "/withdrawals", hasDetail: true, label: "Withdrawal" },
  virtualAccount: { list: "/virtual-accounts", hasDetail: true, label: "Virtual Account" },
  paymentAccount: { list: "/payment-accounts", hasDetail: true, label: "Payment Account" },
  forwarderSweep: { list: "/forwarder-sweeps", hasDetail: true, label: "Forwarder Sweep" },
  offrampBurn: { list: "/offramp-burns", hasDetail: true, label: "Off-Ramp Burn" },
  depositAddress: { list: "/deposit-addresses", hasDetail: true, label: "Deposit Address" },
  stablecoinReceiveAddress: {
    list: "/usdt-receive-addresses",
    hasDetail: true,
    label: "USDT Receive Address",
  },
  crossChainDeposit: {
    list: "/cross-chain-deposits",
    hasDetail: true,
    label: "Cross-Chain Deposit",
  },
  crossChainSend: { list: "/cross-chain-sends", hasDetail: true, label: "Cross-Chain Send" },
  walletMigration: { list: "/migrations", hasDetail: true, label: "Migration" },
  ownerKeyBackup: { list: "/owner-key-backups", hasDetail: true, label: "Owner-Key Backup" },
  kycVerification: { list: "/kyc", hasDetail: true, label: "KYC" },
  ledgerEntry: { list: "/ledger", hasDetail: false, label: "Ledger Entry" },
  walletTransaction: { list: "/transactions", hasDetail: false, label: "Transaction" },
  cardNotification: { list: "/card-notifications", hasDetail: false, label: "Card Notification" },
  earnPosition: { list: "/earn/positions", hasDetail: true, label: "Earn Position" },
  earnOperation: { list: "/earn/operations", hasDetail: true, label: "Earn Operation" },
  vaultApproval: { list: "/earn/approvals", hasDetail: true, label: "Vault Approval" },
  vault: { list: "/earn/vaults", hasDetail: true, label: "Vault" },
};

export function entityLabel(key: EntityKey): string {
  return ENTITY_ROUTES[key].label;
}

export function entityListPath(key: EntityKey): string {
  return ENTITY_ROUTES[key].list;
}

export function entityHasDetail(key: EntityKey): boolean {
  return ENTITY_ROUTES[key].hasDetail;
}

/** Detail URL for an entity, or the list (filtered to the id) if it has no detail screen. */
export function entityPath(key: EntityKey, id?: string | null): string {
  const route = ENTITY_ROUTES[key];
  if (!id) return route.list;
  if (!route.hasDetail) return `${route.list}?id=${encodeURIComponent(id)}`;
  return `${route.list}/${encodeURIComponent(id)}`;
}

export const PATHS = {
  root: "/",
  dashboard: "/dashboard",
  search: "/search",
  audit: "/audit",
  earn: "/earn",
  collateralWithdrawals: "/collateral-withdrawals",
  accountDeletions: "/account-deletions",
  appVersions: "/app-versions",
  featureFlags: "/feature-flags",
  // Fleet view of smart-contract generations + wallets with legacy ERC-1271-capable keys.
  generations: "/generations",
  // Non-terminal / wedged wallet migrations (redrive / abandon / reactivate on the detail).
  stuckMigrations: "/stuck-migrations",
  // Migrated / retired wallets still holding a residual at or above the 0.10 USDC floor.
  oldWalletResiduals: "/old-wallet-residuals",
} as const;

export function accountDeletionPath(id: string): string {
  return `${PATHS.accountDeletions}/${encodeURIComponent(id)}`;
}

export function searchPath(q: string): string {
  return `${PATHS.search}?q=${encodeURIComponent(q)}`;
}
